import {
	Box,
	Card,
	Flex,
	Grid,
	Heading,
	Separator,
	Table,
	Text,
} from '@radix-ui/themes'
import { useOutletContext } from '@remix-run/react'
import * as React from 'react'

import { CartViewItem } from '~/components/common/cart-view-item'
import { CheckoutLink } from '~/components/common/checkout-link.tsx'
import { Price } from '~/components/ui/price.tsx'
import { type GetOrder } from '~/models/ecommerce-providers/sylius/shop/fetch/order/get-order.ts'
import { type GetOrderItem } from '~/models/ecommerce-providers/sylius/shop/fetch/order-item/get-order-item.ts'
import { type ProductOptionValueNormalized } from '~/models/ecommerce-providers/sylius/shop/interface/product-option-value.ts'
import { type ProductVariantNormalized } from '~/models/ecommerce-providers/sylius/shop/interface/product-variant.ts'
import { type ProductNormalized } from '~/models/ecommerce-providers/sylius/shop/interface/product.ts'
import { type Context } from '~/routes/_layout/root.component.tsx'
import { useCart } from '~/utils/hooks/use-cart.ts'

/* -------------------------------------------------------------------------------------------------
 * CartView
 * -----------------------------------------------------------------------------------------------*/

type CartProps = {
	cart: GetOrder
	mutate?: boolean
	isOptimistic?: boolean
}

const CartView: React.FC<CartProps> = (props) => {
	const { cart, mutate = true, isOptimistic = true } = props

	const { locale, setApp } = useOutletContext<Context>()

	const {
		optimisticItems,
		optimisticCount,
		optimisticItemsTotal,
		optimisticShippingTotal,
		optimisticTaxTotal,
		optimisticTotal,
		optimisticPromotionTotal,
		t,
	} = useCart({
		cart,
		isOptimistic,
	})

	return (
		<Card size="4">
			<Box flexGrow="1" mt="4" px={{ initial: '3', lg: '4' }}>
				<Heading
					as="h3"
					mb="5"
					color="gray"
					size={{
						initial: '4',
						xs: '5',
						sm: '6',
					}}
				>
					{mutate
						? t('items.common.cartTitle', { count: optimisticCount })
						: t('items.common.checkoutTitle', { count: optimisticCount })}
				</Heading>
				<Table.Root>
					<Table.Header>
						<Table.Row>
							<Table.ColumnHeaderCell />
							<Table.ColumnHeaderCell>
								<Text color="gray">
									{t('label.name')} / {t('label.variant')}
								</Text>
							</Table.ColumnHeaderCell>
							<Table.ColumnHeaderCell>
								<Text color="gray">{t('label.unitPrice')}</Text>
							</Table.ColumnHeaderCell>
							<Table.ColumnHeaderCell>
								<Text color="gray">{t('label.quantity')}</Text>
							</Table.ColumnHeaderCell>
							<Table.ColumnHeaderCell>
								<Text color="gray">{t('label.total')}</Text>
							</Table.ColumnHeaderCell>
							{mutate && (
								<Table.ColumnHeaderCell>
									<Text color="gray">{t('label.action')}</Text>
								</Table.ColumnHeaderCell>
							)}
						</Table.Row>
					</Table.Header>
					<Table.Body>
						{optimisticItems.map((item: GetOrderItem) => (
							<CartViewItem
								key={(item.variant as ProductVariantNormalized)['@id']!}
                isOptimistic={isOptimistic}
								variantId={(item.variant as ProductVariantNormalized)['@id']!}
								locale={locale}
								mutate={mutate}
								optionValues={
									(item.variant as ProductVariantNormalized).optionValues as
										| ProductOptionValueNormalized[]
										| undefined
								}
								quantity={item.quantity!}
								unitPrice={item.unitPrice!}
								image={
									(item.variant as ProductVariantNormalized).images?.[0] ||
									(
										(item.variant as ProductVariantNormalized)
											.product as ProductNormalized
									).images![0]
								}
								name={item.productName!}
								productSlug={
									(
										(item.variant as ProductVariantNormalized)
											.product as ProductNormalized
									).slug!
								}
								orderItemId={item.id!.toString()}
								setApp={setApp}
							/>
						))}
					</Table.Body>
				</Table.Root>
			</Box>
			<Box
				flexShrink="0"
				position="sticky"
				bottom="0"
				className="z-10 bg-white"
			>
				<Grid
					columns={{ initial: '1', lg: '7' }}
					pt="5"
					pr="1"
					className="border-t-4 border-double border-gray-6"
				>
					{mutate ? (
						<Flex
							direction="column"
							gapY="3"
							className="lg:col-span-3 lg:col-start-5"
						>
							<CheckoutLink
								className=""
								locale={locale}
								setApp={setApp}
								popover={false}
								optimisticItemsTotal={optimisticItemsTotal}
								optimisticShippingTotal={optimisticShippingTotal}
								optimisticTaxTotal={optimisticTaxTotal}
								optimisticTotal={optimisticTotal}
							/>
						</Flex>
					) : (
						<Flex
							direction="column"
							gapY="3"
							className="lg:col-span-3 lg:col-start-5"
						>
							<Table.Root size="1">
								<Table.Body>
									<Table.Row>
										<Table.Cell>
											<Text color="gray" size="2" weight="medium">
												{t('label.subtotal')}
											</Text>
										</Table.Cell>
										<Table.Cell justify="end">
											<Price
												locale={locale}
												color="gray"
												size="2"
												weight="medium"
											>
												{optimisticItemsTotal}
											</Price>
										</Table.Cell>
									</Table.Row>
									<Table.Row>
										<Table.Cell>
											<Text color="gray" size="2" weight="medium">
												{t('label.shipping')}
											</Text>
										</Table.Cell>
										<Table.Cell justify="end">
											<Price
												locale={locale}
												color="gray"
												size="2"
												weight="medium"
											>
												{optimisticShippingTotal}
											</Price>
										</Table.Cell>
									</Table.Row>
									<Table.Row>
										<Table.Cell>
											<Text color="gray" size="2" weight="medium">
												{t('label.taxes')}
											</Text>
										</Table.Cell>
										<Table.Cell justify="end">
											<Price
												locale={locale}
												color="gray"
												size="2"
												weight="medium"
											>
												{optimisticTaxTotal}
											</Price>
										</Table.Cell>
									</Table.Row>
									<Table.Row>
										<Table.Cell>
											<Text color="gray" size="2" weight="medium">
												{t('label.promotion')}
											</Text>
										</Table.Cell>
										<Table.Cell justify="end">
											<Price
												locale={locale}
												color="gray"
												size="2"
												weight="medium"
											>
												{optimisticPromotionTotal}
											</Price>
										</Table.Cell>
									</Table.Row>
								</Table.Body>
							</Table.Root>
							<Separator size="4" />
							<Table.Root size="1">
								<Table.Body>
									<Table.Row>
										<Table.Cell>
											<Text size="3" weight="bold">
												{t('label.total')}
											</Text>
										</Table.Cell>
										<Table.Cell justify="end">
											<Price locale={locale} size="3" weight="bold">
												{optimisticTotal}
											</Price>
										</Table.Cell>
									</Table.Row>
								</Table.Body>
							</Table.Root>
						</Flex>
					)}
				</Grid>
			</Box>
		</Card>
	)
}
CartView.displayName = 'View'

/* -----------------------------------------------------------------------------------------------*/

export { CartView }
